import { useEffect, useRef, useState } from "react";
import { ZoomIn, ZoomOut } from "lucide-react";
import { AuditFlowIcon } from "@/components/chat/auditflow-icon";
import { useFetchSingleDoc } from "@/app/hooks/useFetchSingleDoc";

interface Props {
    doc: { document_id: string; version_id?: string | null } | null;
    rounded?: boolean;
    bordered?: boolean;
}

const ZOOM_MIN = 0.25;
const ZOOM_MAX = 8;
const ZOOM_STEP = 0.25;

export function ImageView({ doc, rounded = true, bordered = true }: Props) {
    const { result, loading, error } = useFetchSingleDoc(
        doc?.document_id ?? null,
        doc?.version_id ?? null,
    );
    const [zoom, setZoom] = useState(1);
    const blobUrlRef = useRef<string | null>(null);

    // Revoke the blob URL when the result changes or the component unmounts
    // so we don't leak memory across tab switches.
    useEffect(() => {
        if (result?.type === "image") {
            blobUrlRef.current = result.blobUrl;
        }
        return () => {
            if (blobUrlRef.current) {
                URL.revokeObjectURL(blobUrlRef.current);
                blobUrlRef.current = null;
            }
        };
    }, [result]);

    const imageUrl = result?.type === "image" ? result.blobUrl : null;

    return (
        <div
            className={`relative flex flex-col flex-1 overflow-hidden ${bordered ? "border border-gray-200" : ""} ${rounded ? "rounded-xl" : ""}`}
        >
            <div className="flex-1 overflow-auto bg-gray-100 flex items-center justify-center p-4">
                {loading && (
                    <AuditFlowIcon spin active size={28} />
                )}
                {error && (
                    <p className="text-sm text-red-500">{error}</p>
                )}
                {imageUrl && (
                    /* eslint-disable-next-line @next/next/no-img-element */
                    <img
                        src={imageUrl}
                        alt={doc?.document_id ?? "image"}
                        style={{
                            transform: `scale(${zoom})`,
                            transformOrigin: "center center",
                            transition: "transform 80ms ease-out",
                        }}
                        className="max-w-full max-h-full shadow-md select-none"
                        draggable={false}
                    />
                )}
            </div>
            {imageUrl && (
                <div className="absolute bottom-4 right-4 flex items-center gap-px rounded-full bg-white/25 backdrop-blur-md border border-white/30 shadow-md px-1 py-1">
                    <button
                        onClick={() =>
                            setZoom((z) =>
                                Math.max(ZOOM_MIN, Math.round((z - ZOOM_STEP) * 100) / 100),
                            )
                        }
                        disabled={zoom <= ZOOM_MIN}
                        className="flex items-center justify-center w-7 h-7 rounded-full text-gray-600 hover:bg-white/80 disabled:opacity-30 transition-colors"
                    >
                        <ZoomOut className="h-3.5 w-3.5" />
                    </button>
                    <span className="text-xs font-medium text-gray-600 tabular-nums w-9 text-center select-none">
                        {Math.round(zoom * 100)}%
                    </span>
                    <button
                        onClick={() =>
                            setZoom((z) =>
                                Math.min(ZOOM_MAX, Math.round((z + ZOOM_STEP) * 100) / 100),
                            )
                        }
                        disabled={zoom >= ZOOM_MAX}
                        className="flex items-center justify-center w-7 h-7 rounded-full text-gray-600 hover:bg-white/80 disabled:opacity-30 transition-colors"
                    >
                        <ZoomIn className="h-3.5 w-3.5" />
                    </button>
                </div>
            )}
        </div>
    );
}
